// Licencias: el estado de un negocio se calcula por fechas (nunca se guarda a mano), salvo que
// el superadmin lo fije con el interruptor manual. Misma regla en SQL (app.license_status):
// los tests de los dos lados usan la tabla LICENSE_CASES.
//
//   vence (pago o prueba) ─► +gracia ─► +solo lectura ─► suspendida
//        activa              en_gracia    solo_lectura

export const LICENSE_STATUSES = {
  activa: { label: 'Activa', canWrite: true },
  en_gracia: { label: 'En gracia', canWrite: true },
  solo_lectura: { label: 'Solo lectura', canWrite: false },
  suspendida: { label: 'Suspendida', canWrite: false },
} as const;

export type LicenseStatus = keyof typeof LICENSE_STATUSES;

export const DEFAULT_TRIAL_DAYS = 14;
export const DEFAULT_GRACE_DAYS = 7;
export const DEFAULT_READONLY_DAYS = 15;

export interface LicenseTerms {
  paid_until: string | null;
  trial_until: string | null;
  grace_days: number;
  readonly_days: number;
  manual_override: LicenseStatus | null;
  /** Plan sin vencimiento (cuentas sin cargo): siempre activa, salvo interruptor manual. */
  no_expiry?: boolean;
}

/** Clave en plans.features que marca un plan sin vencimiento. */
export const NO_EXPIRY_FEATURE = 'no_expiry';

export const planHasNoExpiry = (features: Record<string, unknown> | null | undefined): boolean => features?.[NO_EXPIRY_FEATURE] === true;

export interface LicenseSchedule {
  status: LicenseStatus;
  /** Hasta cuándo está paga (o en prueba). null = nunca tuvo. */
  activeUntil: Date | null;
  graceUntil: Date | null;
  readonlyUntil: Date | null;
  /** El vencimiento que corre es el de la prueba gratis. */
  trial: boolean;
  /** El estado lo fijó el superadmin a mano. */
  override: boolean;
  /** Plan sin vencimiento: no hay fechas que mostrar ni pagos que pedir. */
  noExpiry: boolean;
}

const DAY = 86_400_000;
const parse = (s: string | null) => (s ? new Date(s) : null);

export function licenseSchedule(terms: LicenseTerms, now: Date = new Date()): LicenseSchedule {
  if (terms.no_expiry) {
    const status = terms.manual_override ?? 'activa';
    return { status, activeUntil: null, graceUntil: null, readonlyUntil: null, trial: false, override: !!terms.manual_override, noExpiry: true };
  }
  const paid = parse(terms.paid_until);
  const trial = parse(terms.trial_until);
  const activeUntil = paid && trial ? (paid > trial ? paid : trial) : (paid ?? trial);
  const graceUntil = activeUntil ? new Date(activeUntil.getTime() + terms.grace_days * DAY) : null;
  const readonlyUntil = graceUntil ? new Date(graceUntil.getTime() + terms.readonly_days * DAY) : null;
  let status: LicenseStatus;
  if (terms.manual_override) status = terms.manual_override;
  else if (!activeUntil || !graceUntil || !readonlyUntil) status = 'suspendida';
  else if (now <= activeUntil) status = 'activa';
  else if (now <= graceUntil) status = 'en_gracia';
  else if (now <= readonlyUntil) status = 'solo_lectura';
  else status = 'suspendida';
  return {
    status,
    activeUntil,
    graceUntil,
    readonlyUntil,
    trial: !!trial && (!paid || trial > paid),
    override: !!terms.manual_override,
    noExpiry: false,
  };
}

export const canWriteWith = (status: LicenseStatus): boolean => LICENSE_STATUSES[status].canWrite;

/** Días enteros que faltan hasta una fecha (0 si ya pasó). */
export function daysUntil(date: Date | null, now: Date = new Date()): number {
  if (!date) return 0;
  return Math.max(0, Math.ceil((date.getTime() - now.getTime()) / DAY));
}

/**
 * Nuevo vencimiento al registrar un pago de `months` meses: se cuenta desde el vencimiento
 * actual (pago o prueba) si todavía no pasó, o desde hoy si ya pasó.
 */
export function extendPaidUntil(terms: Pick<LicenseTerms, 'paid_until' | 'trial_until'>, months: number, now: Date = new Date()): Date {
  const candidates = [parse(terms.paid_until), parse(terms.trial_until), now].filter((d): d is Date => !!d);
  const from = new Date(Math.max(...candidates.map((d) => d.getTime())));
  const result = new Date(from);
  result.setUTCMonth(result.getUTCMonth() + months);
  // 31 de enero + 1 mes = último día de febrero (no 3 de marzo).
  if (result.getUTCDate() !== from.getUTCDate()) result.setUTCDate(0);
  return result;
}

/** Límites de un plan: null o ausente = sin límite. */
export interface PlanLimits {
  max_employees?: number | null;
  max_devices?: number | null;
  max_registers?: number | null;
}

export const PLAN_LIMIT_LABELS: Record<keyof PlanLimits, string> = {
  max_employees: 'Empleados',
  max_devices: 'Dispositivos',
  max_registers: 'Cajas',
};

/**
 * Casos compartidos con los tests de SQL: [descripción, términos (fechas relativas a NOW en días),
 * días desde NOW, estado esperado]. NOW = 2026-06-15T12:00:00Z.
 */
export const LICENSE_CASES: Array<
  [string, { paid?: number | null; trial?: number | null; grace?: number; readonly?: number; override?: LicenseStatus | null; noExpiry?: boolean }, LicenseStatus]
> = [
  ['prueba vigente', { trial: 5 }, 'activa'],
  ['prueba vencida hace 3 días (gracia de 7)', { trial: -3 }, 'en_gracia'],
  ['vencida hace 10 días: solo lectura', { paid: -10 }, 'solo_lectura'],
  ['vencida hace 23 días: suspendida', { paid: -23 }, 'suspendida'],
  ['justo en el límite de la gracia', { paid: -7 }, 'en_gracia'],
  ['pago vigente aunque la prueba venció', { trial: -30, paid: 10 }, 'activa'],
  ['sin pago ni prueba', {}, 'suspendida'],
  ['interruptor manual: suspendida aunque esté paga', { paid: 30, override: 'suspendida' }, 'suspendida'],
  ['interruptor manual: activa aunque esté vencida', { paid: -60, override: 'activa' }, 'activa'],
  ['gracia y solo lectura en 0 días', { paid: -1, grace: 0, readonly: 0 }, 'suspendida'],
  ['plan sin vencimiento: activa aunque la prueba venció hace mucho', { trial: -200, noExpiry: true }, 'activa'],
  ['plan sin vencimiento: el interruptor manual igual manda', { trial: -200, noExpiry: true, override: 'suspendida' }, 'suspendida'],
];

export const LICENSE_CASES_NOW = '2026-06-15T12:00:00.000Z';
